import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Download, Clock, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const [order, setOrder] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const { clearCart } = useCart();
  const navigate = useNavigate();
  const pollTimer = useRef(null);

  useEffect(() => {
    if (orderId) {
      // Kick off with an immediate active sync against Maya, then poll
      (async () => {
        try {
          await axios.post(`${API}/orders/${orderId}/sync`);
        } catch (_) {
          /* non-fatal — will fall back to polling */
        }
        checkOrderStatus();
      })();
      clearCart();
    }
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const checkOrderStatus = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);

      if (response.data.status === 'PAYMENT_SUCCESS') {
        fetchDownloads();
        return;
      }

      setPollCount((p) => p + 1);
      // Every 5 poll attempts, re-sync with Maya to recover from a missed webhook
      if ((pollCount + 1) % 5 === 0) {
        try { await axios.post(`${API}/orders/${orderId}/sync`); } catch (_) {}
      }
      pollTimer.current = setTimeout(checkOrderStatus, 3000);
    } catch (error) {
      console.error('Error checking order:', error);
      setLoading(false);
    }
  };

  const manualSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/orders/${orderId}/sync`);
      if (data.status === 'PAYMENT_SUCCESS') {
        await checkOrderStatus();
      } else {
        // Refresh visible status
        const response = await axios.get(`${API}/orders/${orderId}`);
        setOrder(response.data);
      }
    } catch (e) {
      /* swallow */
    } finally {
      setSyncing(false);
    }
  };

  const fetchDownloads = async () => {
    try {
      const sessionId = localStorage.getItem('session_id');
      const response = await axios.get(`${API}/orders/${orderId}/downloads?session_id=${sessionId}`);
      setDownloads(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching downloads:', error);
      setLoading(false);
    }
  };

  const triggerBrowserDownload = (url, filename) => {
    const a = document.createElement('a');
    a.href = url;
    if (filename) a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownload = async (token, albumTitle) => {
    const sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      alert('Session not found — please reopen this page in the same browser you paid on.');
      return;
    }
    try {
      // Fetch metadata to decide between single-file and zip
      const { data } = await axios.get(`${API}/download/${token}?session_id=${encodeURIComponent(sessionId)}`);
      const usableTracks = data.tracks.filter((t) => t.has_file);
      if (usableTracks.length === 0) {
        alert('No downloadable files are attached to this release yet. Please contact support.');
        return;
      }
      const base = `${API}/download/${token}`;
      const q = `session_id=${encodeURIComponent(sessionId)}`;
      if (usableTracks.length === 1) {
        triggerBrowserDownload(`${base}/file?${q}&track=${usableTracks[0].index}`);
      } else {
        triggerBrowserDownload(`${base}/zip?${q}`);
      }
    } catch (error) {
      if (error.response?.status === 410) {
        alert('This download link has expired. Please contact support.');
      } else if (error.response?.status === 404) {
        alert('Download link no longer valid.');
      } else {
        alert('Download failed. Please try again in a moment.');
      }
    }
  };

  if (loading) {
    const stuck = pollCount >= 4; // ~12s without success
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Loader2 className="mx-auto mb-4 text-[#00F0FF] animate-spin" size={48} />
          <p className="text-[#A1A1AA] mb-2">Processing your payment…</p>
          {order && (
            <p className="text-xs text-[#52525B] font-mono" data-testid="processing-order-id">
              Order {order.id?.slice(0, 8)}… · {order.status}
            </p>
          )}
          {stuck && (
            <div className="mt-8 rounded-lg border border-[#27272A] bg-[#0F0F11] p-4 text-left" data-testid="stuck-help">
              <div className="flex items-start gap-2 mb-3">
                <AlertCircle size={16} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-[#A1A1AA] leading-relaxed">
                  Payment confirmation from Maya is delayed. Your money is safe — click below to check with Maya directly.
                </div>
              </div>
              <Button
                onClick={manualSync}
                disabled={syncing}
                className="w-full bg-[#00F0FF] hover:bg-[#33F3FF] text-black rounded-full"
                data-testid="manual-sync-btn"
              >
                <RefreshCw size={14} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Checking Maya…' : 'Check payment status now'}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-[#121214] border border-[#27272A] rounded-lg p-8" data-testid="payment-success-container">
          <div className="text-center mb-8">
            <CheckCircle2 className="mx-auto mb-4 text-[#10B981]" size={64} />
            <h1 className="text-3xl font-heading font-bold mb-2">Payment Successful!</h1>
            <p className="text-[#A1A1AA]">Thank you for your purchase</p>
          </div>

          {order && (
            <div className="space-y-6">
              <div className="border-t border-[#27272A] pt-6">
                <h2 className="font-heading font-semibold mb-4">Order Details</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">Order ID:</span>
                    <span className="font-mono">{order.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">Total Amount:</span>
                    <span className="text-[#00F0FF] font-medium">₱{order.total_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">Status:</span>
                    <span className="text-[#10B981]">{order.status}</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-[#27272A] pt-6">
                <h2 className="font-heading font-semibold mb-4">Your Downloads</h2>
                <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 mb-4 flex items-start gap-3">
                  <Clock className="text-[#00F0FF] flex-shrink-0 mt-0.5" size={20} />
                  <div className="text-sm">
                    <p className="font-medium mb-1">Download links expire in 24 hours</p>
                    <p className="text-[#A1A1AA]">These links are tied to your session and cannot be shared.</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {downloads.map((download) => {
                    const item = order.items.find(i => i.album_id === download.album_id);
                    return (
                      <div key={download.id} className="flex items-center justify-between p-4 bg-[#18181B] rounded-lg">
                        <div>
                          <div className="font-medium">{item?.album_title}</div>
                          <div className="text-sm text-[#A1A1AA]">Ready for download</div>
                        </div>
                        <Button
                          onClick={() => handleDownload(download.token, item?.album_title)}
                          className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black rounded-full"
                          data-testid={`download-button-${download.album_id}`}
                        >
                          <Download size={16} className="mr-2" />
                          Download
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="pt-6">
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full border-[#27272A] hover:bg-[#18181B] rounded-full"
                  data-testid="continue-shopping-button"
                >
                  Continue Shopping
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
